import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { saveAccountKey } from '../lib/account';
import { api, convexClient } from '../lib/convex';
import { errorInfo } from '../lib/convexError';
import k from './frame.module.css';
import { passkeyNoun } from './passkeyNoun';
import { closeButton, dismissAction, mintedKeyAction, type SheetMode } from './sheetState';

/** What the sheet opens as; it switches between the two itself; `passkey` and `key` follow a created account. */
export type SignInMode = 'create' | 'signin';
export type { SheetMode };

/** What `accounts.create` is called with; the content builds it (name typed, label from the user agent). */
export type CreateArgs = { name?: string; label: string };

function title(mode: SheetMode): string {
  switch (mode) {
    case 'create':
      return 'Start building';
    case 'signin':
      return 'Sign in';
    case 'key':
      return 'Save your builder key';
    case 'passkey':
      return `Use ${passkeyNoun(navigator.userAgent)} next time?`;
  }
}

/**
 * Whether this device can make a passkey with Face ID, Touch ID, Windows Hello or the like, read
 * from the platform without the WebAuthn library (the landing's chunk stays free of it, §14.77d).
 * Resolves false where the API is missing or throws.
 */
function platformAuthenticatorAvailable(): Promise<boolean> {
  const pkc = typeof window !== 'undefined' ? window.PublicKeyCredential : undefined;
  if (!pkc?.isUserVerifyingPlatformAuthenticatorAvailable) return Promise.resolve(false);
  return pkc.isUserVerifyingPlatformAuthenticatorAvailable().catch(() => false);
}

const Body = lazy(() => import('./Body'));

/** Fetches the sheet's chunk ahead of the tap (the landing calls it when an opener is hovered or focused). */
export function preloadSignInSheet(): void {
  void import('./Body');
}

/**
 * The one way into the builder without a key (architecture §2, §14.77): a dialog over the landing,
 * a centred modal on a desktop and a bottom sheet on a phone. Native <dialog>, so the focus trap,
 * Escape, the backdrop and inert content behind it come from the browser. It stores the key itself
 * (`saveAccountKey`) and then calls `onSignedIn`; the caller decides where to go. Where the key is
 * stored follows "Keep me signed in on this browser" (checked by default; unchecked keeps it for
 * this tab only, §14.78) — the checkbox is on the create step, the save-your-key step and under the
 * sign-in list, so even a key that arrives after the sheet closed mid-create follows the choice.
 *
 * A minted key is never lost (`sheetState.ts`). The `accounts.create` call is owned here, not by the
 * content, so it outlives the content unmounting; while it is in flight the sheet refuses to close
 * (`cancel` is prevented, the backdrop is ignored, × is disabled). With the checkbox checked (the
 * default), a device that can make a passkey (Face ID, Touch ID, Windows Hello, …) gets the
 * **passkey offer** next: one tap adds one, "Not now" goes on (§14.89); any other device goes
 * straight to the builder, where the wizard sets up a way back in (a passkey or an email). Either
 * way the key is never shown, and stays readable under Security. Chromium's close watcher still
 * closes the dialog on a repeated Escape (after one or two prevented `cancel`s, by its activation
 * accounting), and a key that arrives then is stored at once. Only with the checkbox unchecked does
 * the save-your-key step show, since the key would otherwise die with the tab. The offer and the key
 * step have no ×, and Escape or the backdrop count as "continue": the key is stored, never dropped.
 * The minted key lives in this component's state only until then, and never in a URL or a log.
 */
export function SignInSheet({
  open,
  mode: openAs,
  onClose,
  onSignedIn,
}: {
  open: boolean;
  mode: SignInMode;
  onClose: () => void;
  onSignedIn: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const closedByUs = useRef(false);
  const [mode, setMode] = useState<SheetMode>(openAs);
  const [minted, setMinted] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // "Keep me signed in on this browser" (§14.78), checked by default. The ref mirrors the state so
  // the create call reads the current choice however late its key arrives (even after an unmount);
  // every way the key is stored — the button, Escape, the backdrop, a late arrival — follows it.
  const [keep, setKeep] = useState(true);
  const keepRef = useRef(true);
  const onKeep = (value: boolean) => {
    keepRef.current = value;
    setKeep(value);
  };

  // A fresh session on every open (state adjusted during render, no stale frame). A create still in
  // flight is kept: its key will show here, or be stored if the sheet is closed again by then.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setMode(openAs);
      setMinted(null);
      setCreateError(null);
      setKeep(true);
      keepRef.current = true;
    }
  }

  // Read by the create call when it resolves: is the sheet still open? False once unmounted, so a
  // key that arrives after the landing went away is stored rather than set on nothing.
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
    return () => {
      openRef.current = false;
    };
  }, [open]);

  // Also read then: can this device make a passkey? Asked when the sheet opens, so the answer is in
  // by the time `accounts.create` returns; until it is, the answer is no (the wizard offers it later).
  const platformPasskeys = useRef(false);
  useEffect(() => {
    if (!open) return;
    let live = true;
    void platformAuthenticatorAvailable().then((yes) => {
      if (live) platformPasskeys.current = yes;
    });
    return () => {
      live = false;
    };
  }, [open]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) {
      closedByUs.current = true;
      dialog.close();
    }
  }, [open]);

  // Focus rests on the title when the sheet opens and whenever its screen changes (the browser
  // would otherwise land on the first control, which on a deep link is ×). The title is first in
  // the tab order, × last.
  useEffect(() => {
    if (open) titleRef.current?.focus();
  }, [open, mode]);

  // Whoever signs in goes to the builder next; have its chunk ready.
  useEffect(() => {
    if (open) void import('../builder/routes');
  }, [open]);

  const finish = (key: string) => {
    saveAccountKey(key, { keep: keepRef.current });
    setMinted(null);
    onSignedIn();
  };

  // The create outlives the content and the sheet: whatever happens to them, the key it returns is
  // shown (sheet open) or stored (sheet closed), never dropped. One at a time (the ref, not the
  // state, so a second submit in the same tick is ignored too).
  const inFlight = useRef(false);
  const create = async (args: CreateArgs) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setCreating(true);
    setCreateError(null);
    try {
      const { accountKey } = await convexClient.mutation(api.accounts.create, args);
      const action = mintedKeyAction({ open: openRef.current, keep: keepRef.current, platformPasskeys: platformPasskeys.current });
      if (action === 'store') {
        finish(accountKey);
      } else {
        setMinted(accountKey);
        setMode(action === 'offer' ? 'passkey' : 'key');
      }
    } catch (err) {
      setCreateError(errorInfo(err).message);
    } finally {
      inFlight.current = false;
      setCreating(false);
    }
  };

  const dismissal = () => dismissAction({ mode, minted, creating });

  // Escape, the backdrop or × (the native `close` event); not our own `dialog.close()`.
  const onDialogClose = () => {
    if (closedByUs.current) {
      closedByUs.current = false;
      return;
    }
    const action = dismissal();
    if (action === 'store' && minted !== null) finish(minted);
    // 'close', or 'refuse' when the browser closed it anyway (Chromium's repeated Escape): the caller
    // syncs, and the key arrives to `mintedKeyAction` → store.
    else onClose();
  };

  const close = closeButton({ mode, creating });

  return (
    <dialog
      ref={ref}
      className={k.sheet}
      aria-labelledby="si-title"
      data-testid="signin-sheet"
      onCancel={(e) => {
        if (dismissal() === 'refuse') e.preventDefault();
      }}
      onClose={onDialogClose}
      onClick={(e) => {
        if (e.target === ref.current && dismissal() !== 'refuse') ref.current.close();
      }}
    >
      {open && (
        <div className={k.inner}>
          <h2 className={k.title} id="si-title" tabIndex={-1} ref={titleRef}>
            {title(mode)}
          </h2>
          <Suspense fallback={<div className={k.loading} aria-busy="true" />}>
            <Body
              mode={mode}
              minted={minted}
              creating={creating}
              createError={createError}
              keep={keep}
              onKeep={onKeep}
              onMode={setMode}
              onCreate={(args) => void create(args)}
              onSignedIn={finish}
            />
          </Suspense>
          {close !== 'hidden' && (
            <button
              type="button"
              className={k.close}
              aria-label="Close"
              disabled={close === 'disabled'}
              onClick={() => ref.current?.close()}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>
          )}
        </div>
      )}
    </dialog>
  );
}
